import type { ApiSessionData } from '../api/types';

const DB_NAME = 'safew-session-encryption';
const DB_VERSION = 1;
const STORE_NAME = 'keys';
const SESSION_KEY_ID = 'session-aes-gcm-v1';
const BLOB_PREFIX = 'SWSE1.';
const IV_LENGTH = 12;

let sessionKeyPromise: Promise<CryptoKey> | undefined;

export function isEncryptedStoredSession(value?: unknown): value is string {
  return typeof value === 'string' && value.startsWith(BLOB_PREFIX);
}

export async function encryptStoredSession(sessionData: ApiSessionData) {
  const key = await getSessionKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const plaintext = new TextEncoder().encode(JSON.stringify(sessionData));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  const bytes = new Uint8Array(iv.length + encrypted.byteLength);
  bytes.set(iv, 0);
  bytes.set(new Uint8Array(encrypted), iv.length);

  return `${BLOB_PREFIX}${bytesToBase64(bytes)}`;
}

export async function decryptStoredSession(blob: string): Promise<ApiSessionData | undefined> {
  if (!isEncryptedStoredSession(blob)) return undefined;

  const bytes = base64ToBytes(blob.slice(BLOB_PREFIX.length));
  if (bytes.length <= IV_LENGTH) return undefined;

  const key = await getSessionKey();
  const iv = bytes.slice(0, IV_LENGTH);
  const encrypted = bytes.slice(IV_LENGTH);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);

  return JSON.parse(new TextDecoder().decode(decrypted)) as ApiSessionData;
}

function getSessionKey() {
  if (!crypto?.subtle || typeof indexedDB === 'undefined') {
    throw new Error('[sessionEncryption] WebCrypto or IndexedDB is unavailable');
  }

  sessionKeyPromise ??= loadOrCreateSessionKey().catch((err) => {
    sessionKeyPromise = undefined;
    throw err;
  });
  return sessionKeyPromise;
}

async function loadOrCreateSessionKey() {
  const db = await openDatabase();
  try {
    const existingKey = await getValue<CryptoKey>(db, SESSION_KEY_ID);
    if (existingKey) return existingKey;

    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    ) as CryptoKey;
    return await getOrCreateKey(db, key);
  } finally {
    db.close();
  }
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function getValue<T>(db: IDBDatabase, key: IDBValidKey) {
  return new Promise<T | undefined>((resolve, reject) => {
    const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

function getOrCreateKey(db: IDBDatabase, candidate: CryptoKey) {
  return new Promise<CryptoKey>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(SESSION_KEY_ID);
    let key: CryptoKey;
    // All tabs must use the same key; check and insert in one serialized transaction.
    request.onsuccess = () => {
      key = request.result || candidate;
      if (!request.result) store.add(key, SESSION_KEY_ID);
    };
    transaction.oncomplete = () => resolve(key);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

function bytesToBase64(bytes: Uint8Array) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
